import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { assets } from "../../data/sep26Data";
import { EASE_OUT } from "./hooks";

// Texto a la izquierda, video a la derecha (con borde ovalado que se funde con el fondo).
const CLIPS = [
  { id: "reel", label: "La plantilla", src: assets.reelVideo, poster: assets.reelPoster },
  { id: "equipo", label: "El equipo", src: assets.heroVideo, poster: assets.heroPoster },
  { id: "traje", label: "El traje", src: assets.kitVideo, poster: "/temporada-sep26/traje-poster.jpg" },
  { id: "cancha", label: "La cancha", src: assets.ambientVideo, poster: assets.cancha },
];

export const Multimedia26 = () => {
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const inView = useInView(sectionRef, { margin: "-20% 0px" });
  const [idx, setIdx] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const clip = CLIPS[idx];

  // Reproduce solo cuando la sección está a la vista y el usuario no pausó
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (inView && !userPaused && !reduce) v.play().catch(() => {});
    else v.pause();
  }, [inView, userPaused, reduce, idx]);

  return (
    <section
      id="multimedia"
      ref={sectionRef}
      data-testid="s26-multimedia"
      className="section-pad relative overflow-hidden"
    >
      <div className="wrap grid items-center gap-10 lg:grid-cols-[minmax(300px,0.8fr)_1.5fr]">
        <motion.div
          className="relative z-10"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE_OUT }}
        >
          <h2 className="display text-[clamp(44px,6.2vw,92px)]">
            Un equipo.<br />Un escudo.<br />
            <span style={{ color: "var(--gold)" }}>Muchos lunes.</span>
          </h2>
          <p className="mt-4 label" style={{ color: "var(--gold)" }}>Dentro y fuera de la cancha</p>
          <p className="mt-5 max-w-[40ch]" style={{ color: "var(--muted)" }}>
            Arsenal de Allende juega la Primera División Local de Fútbol 7 en las Canchas Colonias del Norte.
            Conoce a quienes se visten de azul y oro, con su nombre y su número, y revive cómo se ve el equipo en movimiento.
          </p>

          <a href="#plantilla" className="btn mt-7" data-testid="s26-multimedia-cta">Ver la plantilla</a>

          <div role="tablist" aria-label="Videos" className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
            {CLIPS.map((c, i) => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={idx === i}
                data-testid={`s26-video-${c.id}`}
                onClick={() => {
                  setIdx(i);
                  setUserPaused(false);
                }}
                className="label border-b-2 pb-1 transition-colors"
                style={{
                  borderColor: idx === i ? "var(--gold)" : "transparent",
                  color: idx === i ? "var(--bone)" : "var(--muted)",
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Video: se sale hacia el borde derecho y se funde con el fondo */}
        <motion.div
          className="relative lg:-mr-[var(--pad)]"
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE_OUT }}
        >
          <div
            className="relative aspect-[16/10] w-full"
            style={{
              WebkitMaskImage: "radial-gradient(ellipse 72% 80% at 55% 50%, #000 62%, transparent 100%)",
              maskImage: "radial-gradient(ellipse 72% 80% at 55% 50%, #000 62%, transparent 100%)",
            }}
          >
            <AnimatePresence mode="popLayout">
              <motion.video
                key={clip.id}
                ref={videoRef}
                src={clip.src}
                poster={clip.poster}
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 h-full w-full object-cover"
                initial={{ opacity: 0, filter: "blur(6px)" }}
                animate={{ opacity: 1, filter: "blur(0px)" }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                aria-label={clip.label}
                data-testid="s26-multimedia-video"
              />
            </AnimatePresence>
          </div>

          <button
            type="button"
            onClick={() => setUserPaused((v) => !v)}
            aria-label={userPaused ? "Reproducir video" : "Pausar video"}
            data-testid="s26-multimedia-toggle"
            className="absolute bottom-[8%] right-[10%] grid h-12 w-12 place-items-center rounded-full transition-transform active:scale-95"
            style={{ background: "var(--gold)", color: "var(--ink)" }}
          >
            {userPaused || reduce ? <Play size={20} className="translate-x-[1px]" /> : <Pause size={20} />}
          </button>
        </motion.div>
      </div>
    </section>
  );
};
